import { createApiRef, createRouteRef, createSubRouteRef, createPlugin, createApiFactory, discoveryApiRef, identityApiRef, errorApiRef, createRoutableExtension, createComponentExtension, useAnalytics } from '@backstage/core-plugin-api';
import { WebStorage } from '@backstage/core-app-api';
import { ResponseError } from '@backstage/errors';
import { DateTime } from 'luxon';
import React from 'react';
import { Link } from '@backstage/core-components';
import { HighlightedSearchResultText } from '@backstage/plugin-search-react';
import { makeStyles, ListItem, ListItemIcon, ListItemText, Divider } from '@material-ui/core';
import RecordVoiceOverIcon from '@material-ui/icons/RecordVoiceOver';

const lastSeenKey = "user_last_seen_date";
const announcementsApiRef = createApiRef({
  id: "plugin.announcements.service"
});
class DefaultAnnouncementsApi {
  constructor(opts) {
    this.discoveryApi = opts.discoveryApi;
    this.identityApi = opts.identityApi;
    this.webStorage = new WebStorage("announcements", opts.errorApi);
  }
  async fetch(input, init) {
    const baseApiUrl = await this.discoveryApi.getBaseUrl("announcements");
    const { token } = await this.identityApi.getCredentials();
    const headers = new Headers(init == null ? void 0 : init.headers);
    if (token && !headers.has("authorization")) {
      headers.set("authorization", `Bearer ${token}`);
    }
    const request = new Request(`${baseApiUrl}${input}`, {
      ...init,
      headers
    });
    return fetch(request).then(async (response) => {
      if (!response.ok) {
        throw await ResponseError.fromResponse(response);
      }
      return response.json();
    });
  }
  async delete(input, init) {
    const baseApiUrl = await this.discoveryApi.getBaseUrl("announcements");
    const { token } = await this.identityApi.getCredentials();
    const headers = new Headers(init == null ? void 0 : init.headers);
    if (token && !headers.has("authorization")) {
      headers.set("authorization", `Bearer ${token}`);
    }
    const request = new Request(`${baseApiUrl}${input}`, {
      ...{ method: "DELETE" },
      headers
    });
    return fetch(request).then(async (response) => {
      if (!response.ok) {
        throw await ResponseError.fromResponse(response);
      }
    });
  }
  async announcements({ max }) {
    return this.fetch(`/?${max ? `max=${max}` : ""}`);
  }
  async announcementByID(id) {
    return this.fetch(`/${id}`);
  }
  async createAnnouncement(request) {
    return await this.fetch(`/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request)
    });
  }
  async updateAnnouncement(id, request) {
    return this.fetch(`/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request)
    });
  }
  async deleteAnnouncementByID(id) {
    return this.delete(`/${id}`, { method: "DELETE" });
  }
  lastSeenDate() {
    const lastSeen = this.webStorage.get(lastSeenKey);
    if (!lastSeen) {
      return DateTime.fromISO("1990-01-01");
    }
    return DateTime.fromISO(lastSeen);
  }
  markLastSeenDate(date) {
    this.webStorage.set(lastSeenKey, date.toISO());
  }
}

const rootRouteRef = createRouteRef({
  id: "announcements"
});
const announcementCreateRouteRef = createSubRouteRef({
  id: "announcements/create",
  path: "/create",
  parent: rootRouteRef
});
const announcementEditRouteRef = createSubRouteRef({
  id: "announcements/edit",
  path: "/edit/:id",
  parent: rootRouteRef
});
const announcementViewRouteRef = createSubRouteRef({
  id: "announcements/view",
  path: "/view/:id",
  parent: rootRouteRef
});

const announcementsPlugin = createPlugin({
  id: "announcements",
  routes: {
    root: rootRouteRef
  },
  apis: [
    createApiFactory({
      api: announcementsApiRef,
      deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef, errorApi: errorApiRef },
      factory: ({ discoveryApi, identityApi, errorApi }) => {
        return new DefaultAnnouncementsApi({
          discoveryApi,
          identityApi,
          errorApi
        });
      }
    })
  ]
});
const AnnouncementsPage = announcementsPlugin.provide(
  createRoutableExtension({
    name: "AnnouncementsPage",
    component: () => import('./Router-d50f84d5.esm.js').then((m) => m.Router),
    mountPoint: rootRouteRef
  })
);
const AnnouncementsCard = announcementsPlugin.provide(
  createComponentExtension({
    name: "AnnouncementsCard",
    component: {
      lazy: () => import('./index-68d8845b.esm.js').then((m) => m.AnnouncementsCard)
    }
  })
);
const NewAnnouncementBanner = announcementsPlugin.provide(
  createComponentExtension({
    name: "NewAnnouncementBanner",
    component: {
      lazy: () => import('./index-bf5fda6b.esm.js').then((m) => m.NewAnnouncementBanner)
    }
  })
);

const useStyles = makeStyles({
  createdAt: {
    display: "block",
    marginTop: "0.2rem",
    marginBottom: "0.8rem",
    fontSize: "0.8rem"
  },
  excerpt: {
    lineHeight: "1.55"
  },
  itemText: {
    wordBreak: "break-all"
  }
});
const AnnouncementSearchResultListItem = ({ result, rank, highlight }) => {
  const classes = useStyles();
  const analytics = useAnalytics();
  const handleClick = () => {
    analytics.captureEvent("discover", result.title, {
      attributes: { to: result.location },
      value: rank
    });
  };
  const document = result;
  const title = /* @__PURE__ */ React.createElement(Link, {
    noTrack: true,
    to: result.location,
    onClick: handleClick
  }, (highlight == null ? void 0 : highlight.fields.title) ? /* @__PURE__ */ React.createElement(HighlightedSearchResultText, {
    text: highlight.fields.title,
    preTag: highlight.preTag,
    postTag: highlight.postTag
  }) : result.title);
  const excerpt = /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", {
    className: classes.createdAt
  }, "Published ", /* @__PURE__ */ React.createElement("span", {
    title: document.createdAt
  }, DateTime.fromISO(document.createdAt).toRelative())), /* @__PURE__ */ React.createElement(React.Fragment, null, (highlight == null ? void 0 : highlight.fields.text) ? /* @__PURE__ */ React.createElement(HighlightedSearchResultText, {
    text: highlight.fields.text,
    preTag: highlight.preTag,
    postTag: highlight.postTag
  }) : result.text));
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ListItem, {
    alignItems: "center"
  }, /* @__PURE__ */ React.createElement(ListItemIcon, {
    title: "Announcement"
  }, /* @__PURE__ */ React.createElement(RecordVoiceOverIcon, null)), /* @__PURE__ */ React.createElement(ListItemText, {
    primary: title,
    secondary: excerpt,
    className: classes.itemText,
    primaryTypographyProps: { variant: "h6" }
  })), /* @__PURE__ */ React.createElement(Divider, {
    component: "li"
  }));
};

export { AnnouncementSearchResultListItem as A, NewAnnouncementBanner as N, announcementCreateRouteRef as a, announcementsApiRef as b, announcementViewRouteRef as c, announcementEditRouteRef as d, announcementsPlugin as e, AnnouncementsPage as f, AnnouncementsCard as g, rootRouteRef as r };
//# sourceMappingURL=index-4c6f280c.esm.js.map
